import React, { Fragment, useState } from 'react';
import { CalendarIcon, EditIcon } from '../utils/Icons';
import { makeStyles } from '@material-ui/core/styles';
import Rating from '@material-ui/lab/Rating';
import { Dialog, IconButton } from '@material-ui/core';
import RenderThumbnail from '../utils/RenderThumbnail';
import CloseIcon from '@material-ui/icons/Close';
import { DATE_FORMAT_1, DATE_FORMAT_2 } from '../../constants';
import HandoutModal from './HandoutModal';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(advancedFormat);

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    border: 'none',
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
  },
  summaryRoot: {
    padding: '0',
    justifyContent: 'flex-start',
  },
  summaryContent: {
    flexGrow: '0',
  },
  detailsRoot: {
    display: 'block',
  },
  heading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.025em',
  },
  tooltip: {
    color: '#000',
    backgroundColor: '#fff',
    fontSize: '16px',
    padding: '10px',
    boxShadow: '2px 2px 10px 1px rgba(0,0,0,0.2)',
    borderRadius: '8px',
  },
  tooltipArrow: {
    color: 'rgba(100,100,100, 0.6)',
  },
  dialog: {
    width: '70%',
    minWidth: '800px',
    maxHeight: '30rem',
    margin: 'auto',
    '& .MuiPaper-root': {
      width: '100%',
      minWidth: '800px',
      borderRadius: '8px',
    },
  },
}));

function ActivityCard({ cardData }) {
  const classes = useStyles();
  const userType = cardData.userType;
  const [ratingDialog, setRatingDialog] = useState(false);
  const [showHandoutModal, setShowHandoutModal] = useState(false);

  const isHandoutAvailable = () => {
    if (!cardData?.classActivityRecording) return false;
    if (!cardData?.classActivityRecording?.handout) return false;
    if (!cardData?.classActivityRecording?.handout?.link) return false;
    return cardData.classActivityRecording.handout.link.length > 0;
  };
  const handoutAvailable = isHandoutAvailable();

  const isScheduled = cardData.sessionStatus === 'UPCOMING';

  const badgeClass = isScheduled
    ? 'border border-solid border-yellow-c1 rounded-md px-2.5 py-0.5 text-xs font-medium text-yellow-c1'
    : 'border border-solid border-ab-primary border-opacity-20 rounded-md px-2.5 py-0.5 text-xs font-medium text-ab-primary text-opacity-60';

  const cardStatus = {
    DONE: 'Completed',
    UPCOMING: 'Scheduled',
  };

  const handleRatingDialog = () => {
    setRatingDialog((prev) => !prev);
  };
  const activityId = cardData?.activityId?.replaceAll('/', '-');

  return (
    <div className='w-full bg-white text-gray-600 rounded-lg border border-solid border-gray-c1 transition-shadow duration-200 hover:shadow-sm'>
      {/* Top Section */}
      <section className='flex flex-col gap-3 p-6 pb-4'>
        <div className='flex justify-between items-start gap-4'>
          <h1 className='text-xl font-semibold text-ab-primary leading-snug'>{cardData.topic}</h1>
          <div className='flex items-center gap-3 shrink-0'>
            {isScheduled && (
              <span className='inline-flex items-center gap-1.5'>
                <span className='w-2 h-2 rounded-full bg-ab-secondary animate-pulse' />
                <span className='text-xs font-medium text-ab-secondary'>Live</span>
              </span>
            )}
            <span className={badgeClass}>{cardStatus[cardData.sessionStatus]}</span>
          </div>
        </div>
        <div className='flex justify-between items-center'>
          <span className='text-sm text-ab-primary text-opacity-40 font-medium'>
            {cardData.type}
          </span>
          <span className='text-xs text-ab-primary text-opacity-30'>
            Session ID #{cardData.activityId}
          </span>
        </div>
        <div className='flex items-center gap-4'>
          <span className='text-sm text-ab-primary text-opacity-50'>
            Module: <span className='font-medium text-opacity-70'>{cardData.module}</span>
          </span>
        </div>
        <div className='flex items-center gap-3'>
          <CalendarIcon iconStyle='w-4 h-4 text-ab-primary text-opacity-40' />
          <div className='flex items-center gap-2 text-sm'>
            <span className='font-medium text-ab-primary text-opacity-70'>
              {dayjs(cardData.startTime).format('DD MMM, YYYY')}
            </span>
            <span className='text-ab-primary text-opacity-30'>•</span>
            <span className='text-ab-primary text-opacity-50'>
              {dayjs(cardData.startTime).format('h:mm A')} –{' '}
              {dayjs(cardData.endTime).format('h:mm A')}
            </span>
          </div>
        </div>
      </section>
      <hr className='mx-6 border-gray-c1' />
      {/* Coach Section */}
      <section className='flex flex-col gap-4 p-6 py-5'>
        <div className='flex flex-col md:flex-row md:items-stretch gap-3'>
          <div className='border border-solid border-ab-primary border-opacity-10 rounded-lg px-4 py-3 flex items-center gap-3 flex-1'>
            <RenderThumbnail
              name={cardData.instructorName}
              thumbnail={cardData.instructor?.thumbnail}
              size='3'
            />
            <div className='flex flex-col gap-1'>
              <h3 className='font-medium text-sm text-ab-primary'>{cardData.instructorName}</h3>
              <span className='bg-green-c5 text-green-c6 px-2 py-0.5 rounded text-xs font-medium w-fit'>
                Instructor
              </span>
            </div>
          </div>
          {cardData?.moderatorName && (
            <div className='border border-solid border-ab-primary border-opacity-10 rounded-lg px-4 py-3 flex items-center gap-3 flex-1'>
              <RenderThumbnail
                name={cardData.moderatorName}
                thumbnail={cardData.moderator?.thumbnail}
                size='3'
              />
              <div className='flex flex-col gap-1'>
                <h3 className='font-medium text-sm text-ab-primary'>{cardData.moderatorName}</h3>
                <span className='bg-ab-secondary-shade-1 bg-opacity-5 text-ab-secondary-shade-1 text-opacity-70 px-2 py-0.5 rounded text-xs font-medium w-fit'>
                  Moderator
                </span>
              </div>
            </div>
          )}
        </div>
        {/* Canvas & Student Doubts */}
        <div className='flex items-center gap-3 flex-wrap'>
          <button
            className='inline-flex items-center gap-2 px-4 py-2 bg-green-c6 text-ab-tertiary rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 hover:opacity-90 active:scale-[0.98]'
            onClick={() => {
              window.open(`https://canvas.almagrow.com/${activityId}`, '_blank');
            }}
          >
            <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m0 0a15.997 15.997 0 01-1.622 3.395'
              />
            </svg>
            Canvas Board
          </button>
          {cardData?.discussUrl && (
            <button
              className='inline-flex items-center gap-2 px-4 py-2 bg-green-c1 text-ab-tertiary rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 hover:opacity-90 active:scale-[0.98]'
              onClick={() => {
                window.open(`${cardData?.discussUrl}`, '_blank');
              }}
            >
              <svg
                className='w-4 h-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.01 0-3.992.11-5.942.328-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.477.062.957.115 1.44.16'
                />
              </svg>
              Student Group
            </button>
          )}
        </div>
      </section>
      <hr className='mx-6 border-gray-c1' />
      {/* Actions Section */}
      <section className='flex items-center justify-end gap-3 px-6 py-4'>
        {cardData?.sessionType === 'Live' && (
          <button
            className='inline-flex items-center gap-2 px-4 py-2 border border-solid border-ab-primary border-opacity-20 text-ab-primary text-opacity-70 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 hover:opacity-80 hover:border-opacity-30 active:scale-[0.98] bg-transparent'
            onClick={() => setShowHandoutModal(true)}
          >
            {handoutAvailable ? (
              <>
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                </svg>
                View/Edit Handout
              </>
            ) : (
              <>
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5'
                  />
                </svg>
                Upload Handout
              </>
            )}
          </button>
        )}
        {cardData.sessionStatus === 'UPCOMING' && (
          <a
            href={cardData?.joiningLink}
            target='_blank'
            rel='noreferrer'
            className='inline-flex items-center px-8 py-2.5 bg-green-c6 text-ab-tertiary rounded-lg text-sm font-medium cursor-pointer transition-opacity duration-150 hover:opacity-90'
          >
            Start Session
          </a>
        )}
      </section>
      {showHandoutModal && (
        <HandoutModal
          showModal={showHandoutModal}
          setShowModal={setShowHandoutModal}
          data={cardData}
          refreshData={() => window.location.reload()}
          handoutLinkAvailable={handoutAvailable}
        />
      )}
      <RatingDialog
        classes={classes}
        cardData={cardData}
        userType={userType}
        open={ratingDialog}
        onClose={handleRatingDialog}
      />
    </div>
  );
}

export default ActivityCard;

const RatingDialog = ({ classes, cardData, userType, open, onClose }) => {
  const [section, setSection] = useState('ratings');
  const complimentUserType = userType === 'moderator' ? 'instructor' : 'moderator';
  const [activeOption, setActiveOption] = useState('Students');
  const activeClass =
    'p-3 text-ab-secondary font-medium cursor-pointer border-b-2 border-solid border-ab-secondary rounded';
  const inactiveClass =
    'p-3 text-ab-primary text-opacity-40 font-medium cursor-pointer border-b-2 border-solid border-transparent rounded';

  const activeSectionClass = 'bg-ab-secondary text-ab-tertiary';

  const handleSectionChange = (value) => {
    setSection(value);
  };

  return (
    <Dialog className={classes.dialog} open={open} onClose={onClose} maxWidth='80vw'>
      {/* Main Switcher */}
      <div className='flex item-center gap-2 w-full border border-solid border-gray-300 shadow-lg px-4 relative'>
        <span
          className={activeOption === 'Students' ? activeClass : inactiveClass}
          onClick={() => setActiveOption('Students')}
        >
          Students
        </span>
        <span
          className={activeOption === 'userType' ? activeClass : inactiveClass}
          onClick={() => setActiveOption('userType')}
        >
          {complimentUserType.charAt(0).toUpperCase() + complimentUserType.slice(1)}
        </span>
        <span>
          <IconButton
            size='small'
            onClick={onClose}
            style={{ position: 'absolute', right: '10px', top: '10px' }}
          >
            <CloseIcon />
          </IconButton>
        </span>
      </div>
      {/* Section Switcher */}
      <div className='flex justify-evenly shadow-md m-auto mt-6 mb-4'>
        <span
          className={`px-10 py-2 cursor-pointer rounded-l-lg ${
            section === 'ratings' && activeSectionClass
          }`}
          onClick={() => handleSectionChange('ratings')}
        >
          Ratings
        </span>
        <span
          className={`px-10 py-2 cursor-pointer rounded-r-lg ${
            section === 'feedback' && activeSectionClass
          }`}
          onClick={() => handleSectionChange('feedback')}
        >
          Feedback
        </span>
      </div>
      {activeOption === 'Students' ? (
        <div className='py-4 px-8'>
          {section === 'ratings' && (
            <div className='grid grid-cols-4 gap-x-20 gap-y-2 mb-5'>
              {cardData?.feedbacks?.studentFeedback
                ? Object.values(cardData?.feedbacks?.studentFeedback).map((item) => {
                    if (typeof item === 'object' && item !== null) {
                      return Object.keys(item).map((key, i) => {
                        if (key !== 'remarks') {
                          return (
                            <Fragment key={i}>
                              <h3 className='text-gray-600 col-span-3'>{key}</h3>
                              <Rating
                                className='col-span-1'
                                name='read-only'
                                value={item[key]}
                                readOnly
                              />
                            </Fragment>
                          );
                        }
                        return null;
                      });
                    }
                    return null;
                  })
                : null}
            </div>
          )}
          {section === 'feedback' && (
            <div className='mb-5'>
              {cardData?.feedbacks?.studentFeedback
                ? Object.values(cardData.feedbacks.studentFeedback).map((item) => {
                    if (typeof item === 'object' && item !== null) {
                      return Object.keys(item).map((key, i) => {
                        if (key === 'remarks') {
                          if (item[key] === '') return null;
                          return (
                            <Fragment key={i}>
                              <h3 className='font-medium inline mr-10'>
                                {cardData[`${complimentUserType}Name`]}
                              </h3>
                              <span className='text-sm text-gray-600'>
                                {dayjs(
                                  cardData?.feedbacks[`${complimentUserType}Feedback`].createdAt
                                ).format(DATE_FORMAT_2)}
                              </span>
                              <h3 className='text-gray-600 col-span-3'>{item[key]}</h3>
                            </Fragment>
                          );
                        }
                        return null;
                      });
                    }
                    return (
                      <div key={item}>
                        <h3 className='font-medium'>No feedback available</h3>
                      </div>
                    );
                  })
                : null}
            </div>
          )}
        </div>
      ) : (
        <div className='py-4 px-8'>
          {section === 'ratings' && (
            <div className='grid grid-cols-4 gap-x-20 gap-y-2 mb-5'>
              {cardData?.feedbacks[`${complimentUserType}Feedback`]
                ? Object.values(cardData.feedbacks[`${complimentUserType}Feedback`]).map((item) => {
                    if (typeof item === 'object' && item !== null) {
                      return Object.keys(item).map((key, i) => {
                        if (key !== 'remarks') {
                          return (
                            <Fragment key={i}>
                              <h3 className='text-gray-600 col-span-3'>{key}</h3>
                              <Rating
                                className='col-span-1'
                                name='read-only'
                                value={item[key]}
                                readOnly
                              />
                            </Fragment>
                          );
                        }
                        return null;
                      });
                    }
                    return null;
                  })
                : null}
            </div>
          )}
          {section === 'feedback' && (
            <div className='mb-5'>
              {cardData?.feedbacks[`${complimentUserType}Feedback`]
                ? Object.values(cardData.feedbacks[`${complimentUserType}Feedback`]).map((item) => {
                    if (typeof item === 'object' && item !== null) {
                      return Object.keys(item).map((key, i) => {
                        if (key === 'remarks') {
                          if (item[key] === '') return null;
                          return (
                            <Fragment key={i}>
                              <h3 className='font-medium inline mr-10'>
                                {cardData[`${complimentUserType}Name`]}
                              </h3>
                              <span className='text-sm text-gray-600'>
                                {dayjs(
                                  cardData.feedbacks[`${complimentUserType}Feedback`].createdAt
                                ).format(DATE_FORMAT_2)}
                              </span>
                              <h3 className='text-gray-600 col-span-3'>{item[key]}</h3>
                            </Fragment>
                          );
                        }
                        return null;
                      });
                    }
                    return (
                      <div key={item}>
                        <h3 className='font-medium'>No feedback available</h3>
                      </div>
                    );
                  })
                : null}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
